import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Users, Download, Video } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";
import WellnessDisclaimer from "@/components/WellnessDisclaimer";
import Seo from "@/components/Seo";

const MONTHS_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function formatDate(d) {
    return new Date(d).toLocaleString("fr-FR", {
        weekday: "long", day: "numeric", month: "long",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function EventsPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const [calendars, setCalendars] = useState([]);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        http.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
        http.get("/calendars").then((r) => setCalendars(r.data)).catch(() => {});
    }, []);

    const handleRegister = async (ev) => {
        if (!user || user === false) {
            toast.info("Connectez-vous pour vous inscrire.");
            navigate("/connexion");
            return;
        }
        // Ask the backend first: it decides whether the user is a subscriber (free),
        // already registered, or must pay. If paid, open the payment popup — which
        // now hosts the optional gift-code entry itself, just like the subscription flow.
        try {
            const reg = await http.post(`/events/${ev.id}/register`, {});
            if (reg.data.free) {
                if (reg.data.status === "already_registered") {
                    toast.warning(
                        "Vous êtes déjà inscrit à cet évènement — pour toute question en lien avec l'atelier à venir (lien vers l'évènement ou autres), veuillez nous écrire à reservation@foundation-of-light.com",
                        { duration: 12000 }
                    );
                } else if (reg.data.free_reason === "free_event") {
                    toast.success(
                        "Merci d'apporter votre lumière à cet évènement ✨ Un email de confirmation vient de partir avec le lien de connexion et les liens d'ajout à vos calendriers — à très bientôt.",
                        { duration: 14000 }
                    );
                } else {
                    toast.success(
                        "Inscription automatique grâce à votre abonnement — vous recevrez le lien 3 jours avant la date de l'évènement. Assurez-vous que l'adresse reservation@foundation-of-light.com est bien enregistrée dans vos contacts pour éviter que l'email avec le lien soit routé vers les courriers indésirables (spam).",
                        { duration: 14000 }
                    );
                }
                return;
            }
            const priceCents = Math.round((reg.data.price || ev.price) * 100);
            setDialog({
                kind: "event",
                event_id: ev.id,
                item_name: `Événement : ${ev.title}`,
                amount: priceCents,
                origin_url: window.location.origin,
            });
        } catch (e) {
            // 409 = duplicate registration in progress — long message that needs
            // time to be read (email pickup delay, spam-folder check, contact info).
            const isDup = e?.response?.status === 409;
            toast.error(formatApiError(e), { duration: isDup ? 15000 : 6000 });
        }
    };

    // Called by PaymentMethodDialog. Two branches:
    //  - With `discount_code` → user typed a gift code inside the popup → register the
    //    event for free through POST /events/{id}/register with the code.
    //  - Without → normal paid flow (Stripe checkout redirect).
    const stripeCheckout = async (opts) => {
        if (opts?.discount_code) {
            const { data } = await http.post(`/events/${dialog.event_id}/register`, {
                discount_code: opts.discount_code,
            });
            if (data.free) {
                toast.success(`🎁 Atelier offert grâce au code ${opts.discount_code} — confirmation envoyée par email`);
                setDialog(null);
                return;
            }
        }
        const { data } = await http.post("/payments/checkout", {
            event_id: dialog.event_id,
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-20" data-testid="events-page">
            <Seo
                title="Ateliers & cercles à venir · Foundation of Light"
                description="Ateliers de soins énergétiques avec le langage de lumière en groupe et cercles d'éveil de conscience à venir. Inscription en ligne, replays et abonnements."
                path="/evenements"
                jsonLd={events.length ? {
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "itemListElement": events.slice(0, 20).map((ev, i) => ({
                        "@type": "ListItem",
                        "position": i + 1,
                        "item": {
                            "@type": "Event",
                            "name": ev.title,
                            "description": ev.description,
                            "startDate": ev.starts_at,
                            "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
                            "eventStatus": "https://schema.org/EventScheduled",
                            "location": {
                                "@type": "VirtualLocation",
                                "url": ev.external_join_url || ev.zoom_join_url || "https://foundation-of-light.com/evenements",
                            },
                            "organizer": {
                                "@type": "Organization",
                                "name": "Foundation of Light",
                                "url": "https://foundation-of-light.com",
                            },
                            "offers": {
                                "@type": "Offer",
                                "price": ev.price,
                                "priceCurrency": "EUR",
                                "availability": (ev.registered_count || 0) < (ev.capacity || 50) ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
                                "url": `https://foundation-of-light.com/evenements`,
                            },
                        },
                    })),
                } : null}
            />
            <div className="mb-16">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Événements</div>
                <h1 className="font-serif text-5xl sm:text-6xl">Ateliers & cercles à venir</h1>
                <p className="text-lg text-foreground/70 mt-4 max-w-2xl">
                    Découvrez les différents types d'ateliers de soins individuels en groupe. Chaque série d'ateliers propose un point d'attention particulier (Harmonisation, Éveil, Libération…). D'autres évènements et cercles de lumière feront leurs apparitions de temps à autres sur cette page. Des formules d'abonnement pour soutenir activement les travaux de diffusion de la Foundation of Light donnent droit à l'accès gratuit à certaines séries et à une réduction de -33% sur toutes les autres.
                </p>
                <a
                    href="/abonnements"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-5 text-primary font-medium underline decoration-primary/40 underline-offset-4 hover:decoration-primary transition-colors group"
                    data-testid="events-intro-subscriptions-link"
                >
                    Je découvre les formules d'abonnements
                    <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">→</span>
                </a>
                {calendars.length > 0 && (
                    <div className="mt-10 rounded-3xl border border-secondary/30 bg-secondary/5 p-6 md:p-7 max-w-3xl" data-testid="calendar-downloads">                        <div className="flex items-center gap-2 mb-3">
                            <Download className="w-4 h-4 text-secondary" />
                            <h3 className="font-serif text-xl">Calendrier PDF à télécharger</h3>
                        </div>
                        <p className="text-sm text-foreground/70 mb-4">
                            Un aperçu complet du mois — dates, horaires, tarifs et instructions de réservation. Le calendrier du mois suivant devient disponible une dizaine de jours avant son début.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {calendars.map((c) => {
                                const label = `${MONTHS_FR[c.month]} ${c.year}`;
                                const backend = process.env.REACT_APP_BACKEND_URL;
                                const url = `${backend}/api/calendars/${c.year_month}`;
                                // Human-readable last update (Europe/Paris implicit via locale)
                                const updated = c.generated_at
                                    ? new Date(c.generated_at).toLocaleDateString("fr-FR", {
                                          day: "numeric", month: "long", year: "numeric",
                                          hour: "2-digit", minute: "2-digit",
                                      })
                                    : null;
                                return (
                                    <div key={c.year_month} className="flex flex-col items-start gap-1">
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 rounded-full h-11 px-5 bg-secondary text-secondary-foreground text-sm font-medium hover:scale-[1.02] transition-transform"
                                            data-testid={`calendar-download-${c.year_month}`}
                                        >
                                            <Download className="w-4 h-4" /> {label}
                                        </a>
                                        {updated && (
                                            <span
                                                className="text-[11px] text-foreground/55 pl-1"
                                                data-testid={`calendar-updated-${c.year_month}`}
                                                title={`Dernière mise à jour : ${updated}`}
                                            >
                                                Mis à jour le {updated}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-xs text-foreground/60 italic leading-relaxed mt-4 pt-4 border-t border-secondary/20" data-testid="calendar-update-notice">
                            Le calendrier est mis à jour au fil de l'eau pour refléter les ateliers nouvellement ouverts, les changements d'horaires ou les répliques ajoutées. La date de mise à jour figure en bas à droite du document — pensez à revenir télécharger la version la plus récente pour ne rien manquer du chemin proposé.
                        </p>
                    </div>
                )}
            </div>
            {loading ? (
                <div className="text-muted-foreground">Chargement…</div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {events.map((ev, i) => (
                        <motion.article
                            key={ev.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="group border border-border/60 rounded-3xl overflow-hidden bg-card hover:shadow-xl transition-shadow"
                            data-testid={`event-card-${ev.id}`}
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <Link to={`/evenements/${ev.id}`} data-testid={`event-image-link-${ev.id}`} aria-label={`Lire la description complète de ${ev.title}`}>
                                    <img src={ev.image_url} alt={ev.image_alt || ev.title} title={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                                </Link>
                            </div>
                            <div className="p-6">
                                <div className="text-xs tracking-[0.2em] uppercase text-primary mb-3">{ev.category}</div>
                                <Link to={`/evenements/${ev.id}`} className="hover:text-primary transition-colors" data-testid={`event-title-link-${ev.id}`}>
                                    <h3 className="font-serif text-2xl mb-3">{ev.title}</h3>
                                </Link>
                                <p className="text-sm text-foreground/70 mb-1 line-clamp-2">{ev.description}</p>
                                <Link
                                    to={`/evenements/${ev.id}`}
                                    className="inline-flex items-center gap-1 text-xs text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary mb-4 group/link"
                                    data-testid={`event-read-more-${ev.id}`}
                                >
                                    Lire la description complète
                                    <span aria-hidden="true" className="transition-transform group-hover/link:translate-x-0.5">→</span>
                                </Link>
                                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-4 mt-3">
                                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(ev.starts_at)}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {ev.duration_minutes} min</span>
                                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {ev.registered_count}/{ev.capacity}</span>
                                </div>
                                {ev.replay_available && (
                                    <div className="inline-flex items-center gap-2 text-xs font-medium text-white bg-primary rounded-full px-3 py-1.5 mb-4 shadow-sm" data-testid={`replay-badge-${ev.id}`}>
                                        <Video className="w-3.5 h-3.5" strokeWidth={2.5} /> Replay disponible
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <div className="font-serif text-2xl">{ev.price > 0 ? `${ev.price}€` : "Gratuit"}</div>
                                    <Button
                                        onClick={() => handleRegister(ev)}
                                        className="rounded-full bg-primary text-primary-foreground"
                                        data-testid={`register-event-${ev.id}`}
                                    >
                                        S'inscrire
                                    </Button>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            )}
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={stripeCheckout}
                allowGiftCode={true}
                giftCodeScope="event"
            />
            <WellnessDisclaimer />
        </div>
    );
}
